import { isNil } from 'ramda';
import { BaseApiClient } from '@/services/baseApiClient';
import type { RequestOptions } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import type { PopulatedSerializedNotificationSettings } from '@enmon/commons/lib/enmon';
import type { INotificationSettingsApiClient } from './types/interface';
import type {
  CreateNotificationSettingsArg,
  GetNotificationRulesArg,
  GetNotificationSettingWatchedArg,
  GetNotificationSettingsArg,
  GetOneNotificationSettingsArg,
  GetNotificationRulesPreviewArg,
  SetNotificationSettingsMutedArg,
  UpdateNotificationSettingsArg,
} from './types/args';
import type {
  CreateNotificationSettingsResult,
  GetNotificationRulesResult,
  GetNotificationSettingWatchedResult,
  GetNotificationSettingsResult,
  GetMyNotificationMutesResult,
  GetOneNotificationSettingsResult,
  GetNotificationRulesPreviewResult,
  SetNotificationSettingsMutedResult,
  UpdateNotificationSettingsResult,
} from './types/results';

export class NotificationSettingsApiClient extends BaseApiClient implements INotificationSettingsApiClient {
  constructor() {
    super('api/notification-settings');
  }

  public get<T = PopulatedSerializedNotificationSettings>(
    filter: GetNotificationSettingsArg,
    signal: RequestOptions['signal'],
  ): Promise<GetNotificationSettingsResult<T>> {
    return this.http.get('', { query: getQuery(filter), signal });
  }

  public getOne(
    id: GetOneNotificationSettingsArg,
    signal: RequestOptions['signal'],
  ): Promise<GetOneNotificationSettingsResult> {
    return this.http.get(`${id}`, { signal });
  }

  public create(arg: CreateNotificationSettingsArg): Promise<CreateNotificationSettingsResult> {
    return this.http.post('', arg);
  }

  public update({
    notificationsSettingsId,
    payload,
  }: UpdateNotificationSettingsArg): Promise<UpdateNotificationSettingsResult> {
    return this.http.put(`${notificationsSettingsId}`, payload);
  }

  public setMuted({ id, muted }: SetNotificationSettingsMutedArg): Promise<SetNotificationSettingsMutedResult> {
    return muted ? this.http.post(`${id}/mute`, {}) : this.http.delete(`${id}/mute`);
  }

  public getMutedByMe(signal: RequestOptions['signal']): Promise<GetMyNotificationMutesResult> {
    return this.http.get('muted-by-me', { signal });
  }

  public getRulesPreview(
    { settingId, draft, ...pagination }: GetNotificationRulesPreviewArg,
    signal: RequestOptions['signal'],
  ): Promise<GetNotificationRulesPreviewResult> {
    return this.http.post(isNil(settingId) ? 'rules/preview' : `${settingId}/rules/preview`, draft, {
      query: getQuery(pagination),
      signal,
    });
  }

  public getRules(
    { id }: GetNotificationRulesArg,
    signal: RequestOptions['signal'],
  ): Promise<GetNotificationRulesResult> {
    return this.http.get(`${id}/rules`, { signal });
  }

  public getWatched(
    id: GetOneNotificationSettingsArg,
    filter: GetNotificationSettingWatchedArg,
    signal: RequestOptions['signal'],
  ): Promise<GetNotificationSettingWatchedResult> {
    return this.http.get(`${id}/watched`, { query: getQuery(filter), signal });
  }
}
